import { useState, useEffect } from 'react';

interface PaginatedData<T> {
  items: T[];
  pagination?: { total?: number };
}

/**
 * Hook to accumulate paginated items with de-duplication.
 * The caller manages offset state and passes it to both the API query and this hook.
 */
export const usePaginatedItems = <T extends { id: string }>(
  data: PaginatedData<T> | undefined,
  offset: number,
): T[] => {
  const [items, setItems] = useState<T[]>([]);

  useEffect(() => {
    if (!data?.items) return;

    if (offset === 0) {
      setItems(data.items);
    } else {
      setItems((prev) => {
        const combined = [...prev, ...data.items];
        const uniqueMap = new Map(combined.map((item) => [item.id, item]));
        return Array.from(uniqueMap.values());
      });
    }
  }, [data?.items, offset]);

  return items;
};
